import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@core/environments/environment';
import { IUserGroup } from '@core/interfaces/user-group.interface';
import { ApplicationPerformanceService } from '@core/services/application-performance.service';
import { UserGroupService } from '@core/services/user-group.service';
import { ComponentStore } from '@ngrx/component-store';
import { catchError, EMPTY, forkJoin, map, Observable, switchMap, tap } from 'rxjs';
import {
  IApplicationPerformanceKpis,
  IApplicationPerformanceKpisPerUser,
  IApplicationPerformanceKpisPerUserGroup,
  IEventAnalyticsQueryParams,
  IEventAnalyticsScopeState,
} from './admin-event-analytics.interface';
import { AdminEventAnalyticsService } from './admin-event-analytics.service';

/** The Groups/Users tabs rank everything client-side, so we always ask the backend for the full page. */
const MAX_PAGE_SIZE = 1000;

interface IAdminEventAnalyticsState {
  scope: IEventAnalyticsScopeState;

  userGroups: IUserGroup[] | null;
  userGroupsLoading: boolean;

  kpis: IApplicationPerformanceKpis | null;
  compareKpis: IApplicationPerformanceKpis | null;
  kpisLoading: boolean;

  kpisPerUserGroup: IApplicationPerformanceKpisPerUserGroup[] | null;
  compareKpisPerUserGroup: IApplicationPerformanceKpisPerUserGroup[] | null;
  groupsLoading: boolean;

  kpisPerUser: IApplicationPerformanceKpisPerUser[] | null;
  compareKpisPerUser: IApplicationPerformanceKpisPerUser[] | null;
  usersLoading: boolean;
}

const initialState: IAdminEventAnalyticsState = {
  compareKpis: null,
  compareKpisPerUser: null,
  compareKpisPerUserGroup: null,
  groupsLoading: false,
  kpis: null,
  kpisLoading: false,
  kpisPerUser: null,
  kpisPerUserGroup: null,
  scope: {
    compareRange: null,
    endDate: null,
    selectedUserGroupIds: new Set<string>(),
    startDate: null,
  },
  userGroups: null,
  userGroupsLoading: false,
  usersLoading: false,
};

@Injectable()
export class AdminEventAnalyticsStore extends ComponentStore<IAdminEventAnalyticsState> {
  public readonly scope$ = this.selectSignal((state) => state.scope);
  /** Whether a compare range is active - passed down to KPI cards so they can show a loading placeholder for the delta badge instead of hiding it while the comparison data is still in flight. */
  public readonly isComparing$ = this.selectSignal((state) => state.scope.compareRange !== null);

  public readonly userGroups$ = this.selectSignal((state) => state.userGroups);
  public readonly userGroupsLoading$ = this.selectSignal((state) => state.userGroupsLoading);

  public readonly kpis$ = this.selectSignal((state) => state.kpis);
  public readonly compareKpis$ = this.selectSignal((state) => state.compareKpis);
  public readonly kpisLoading$ = this.selectSignal((state) => state.kpisLoading);

  public readonly kpisPerUserGroup$ = this.selectSignal((state) => state.kpisPerUserGroup);
  public readonly compareKpisPerUserGroup$ = this.selectSignal((state) => state.compareKpisPerUserGroup);
  public readonly groupsLoading$ = this.selectSignal((state) => state.groupsLoading);

  public readonly kpisPerUser$ = this.selectSignal((state) => state.kpisPerUser);
  public readonly compareKpisPerUser$ = this.selectSignal((state) => state.compareKpisPerUser);
  public readonly usersLoading$ = this.selectSignal((state) => state.usersLoading);

  public readonly setScope = this.updater(
    (state, scope: IEventAnalyticsScopeState): IAdminEventAnalyticsState => ({
      ...state,
      scope,
    }),
  );

  public readonly fetchUserGroups = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => this.patchState({ userGroupsLoading: true })),
      switchMap(() =>
        this.userGroupService.getCurrentUserGroups(new HttpParams().set('limit', '1000').set('order_by', 'name')).pipe(
          tap((userGroups) => this.patchState({ userGroups, userGroupsLoading: false })),
          catchError(() => this.handleError({ userGroupsLoading: false })),
        ),
      ),
    ),
  );

  public readonly fetchKpis = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => this.patchState({ kpisLoading: true })),
      switchMap(() => {
        const { scope } = this.get();
        const params = this.buildQueryParams(scope);

        const request$: Observable<[IApplicationPerformanceKpis, IApplicationPerformanceKpis | null]> =
          scope.compareRange
            ? forkJoin([
                this.getKpis(params),
                this.getKpis({
                  ...params,
                  endDate: scope.compareRange.endDate,
                  startDate: scope.compareRange.startDate,
                }),
              ])
            : this.getKpis(params).pipe(map((kpis): [IApplicationPerformanceKpis, null] => [kpis, null]));

        return request$.pipe(
          tap(([kpis, compareKpis]) => this.patchState({ compareKpis, kpis, kpisLoading: false })),
          catchError(() => this.handleError({ kpisLoading: false })),
        );
      }),
    ),
  );

  public readonly fetchKpisPerUserGroup = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => this.patchState({ groupsLoading: true })),
      switchMap(() => {
        const { scope } = this.get();
        const params = this.buildQueryParams(scope);

        const request$: Observable<
          [IApplicationPerformanceKpisPerUserGroup[], IApplicationPerformanceKpisPerUserGroup[] | null]
        > = scope.compareRange
          ? forkJoin([
              this.getKpisPerUserGroup(params),
              this.getKpisPerUserGroup({
                ...params,
                endDate: scope.compareRange.endDate,
                startDate: scope.compareRange.startDate,
              }),
            ])
          : this.getKpisPerUserGroup(params).pipe(
              map((kpisPerUserGroup): [IApplicationPerformanceKpisPerUserGroup[], null] => [kpisPerUserGroup, null]),
            );

        return request$.pipe(
          tap(([kpisPerUserGroup, compareKpisPerUserGroup]) =>
            this.patchState({ compareKpisPerUserGroup, groupsLoading: false, kpisPerUserGroup }),
          ),
          catchError(() => this.handleError({ groupsLoading: false, kpisPerUserGroup: [] })),
        );
      }),
    ),
  );

  public readonly fetchKpisPerUser = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      tap(() => this.patchState({ usersLoading: true })),
      switchMap(() => {
        const { scope } = this.get();
        const params = this.buildQueryParams(scope);

        const request$: Observable<
          [IApplicationPerformanceKpisPerUser[], IApplicationPerformanceKpisPerUser[] | null]
        > = scope.compareRange
          ? forkJoin([
              this.getKpisPerUser(params),
              this.getKpisPerUser({
                ...params,
                endDate: scope.compareRange.endDate,
                startDate: scope.compareRange.startDate,
              }),
            ])
          : this.getKpisPerUser(params).pipe(
              map((kpisPerUser): [IApplicationPerformanceKpisPerUser[], null] => [kpisPerUser, null]),
            );

        return request$.pipe(
          tap(([kpisPerUser, compareKpisPerUser]) =>
            this.patchState({ compareKpisPerUser, kpisPerUser, usersLoading: false }),
          ),
          catchError(() => this.handleError({ kpisPerUser: [], usersLoading: false })),
        );
      }),
    ),
  );

  private readonly userGroupService = inject(UserGroupService);
  private readonly mockService = inject(AdminEventAnalyticsService);
  private readonly applicationPerformanceService = inject(ApplicationPerformanceService);

  /**
   * Deterministic mock data (`AdminEventAnalyticsService`) instead of the real, HTTP-backed
   * endpoints - driven by `environment.mockUsageAdoptionData`, which only the `e2e` build
   * configuration sets (see `environment.e2e.ts`). Every other build keeps using real data,
   * same as before this was env-driven.
   */
  private readonly useMockData = environment.mockUsageAdoptionData ?? false;

  constructor() {
    super(initialState);
  }

  /**
   * Leaves the "no explicit selection" case as an empty list - `toHttpParams` then omits the
   * `user_group_ids` param entirely rather than sending every known id, since the backend already
   * returns the unfiltered result set when the param is absent.
   */
  private buildQueryParams(scope: IEventAnalyticsScopeState): IEventAnalyticsQueryParams {
    return { endDate: scope.endDate, startDate: scope.startDate, userGroupIds: Array.from(scope.selectedUserGroupIds) };
  }

  private getKpis(params: IEventAnalyticsQueryParams): Observable<IApplicationPerformanceKpis> {
    if (this.useMockData) {
      return this.mockService.getApplicationPerformanceKpis(params);
    }

    return this.applicationPerformanceService.getApplicationPerformance(this.toHttpParams(params));
  }

  private getKpisPerUserGroup(
    params: IEventAnalyticsQueryParams,
  ): Observable<IApplicationPerformanceKpisPerUserGroup[]> {
    if (this.useMockData) {
      return this.mockService.getApplicationPerformanceKpisPerUserGroup(params);
    }

    return this.applicationPerformanceService
      .getApplicationPerformanceByUserGroup(this.toHttpParams(params, MAX_PAGE_SIZE))
      .pipe(map((response) => response.data ?? []));
  }

  private getKpisPerUser(params: IEventAnalyticsQueryParams): Observable<IApplicationPerformanceKpisPerUser[]> {
    if (this.useMockData) {
      return this.mockService.getApplicationPerformanceKpisPerUser(params);
    }

    return this.applicationPerformanceService
      .getApplicationPerformanceByUser(this.toHttpParams(params, MAX_PAGE_SIZE))
      .pipe(map((response) => response.data ?? []));
  }

  /** Builds the real API's query params from an already-resolved `IEventAnalyticsQueryParams` - shared by every non-mock request, with an optional page size override for the paginated group/user breakdowns. */
  private toHttpParams(params: IEventAnalyticsQueryParams, limit?: number): HttpParams {
    let httpParams = new HttpParams();

    if (params.startDate) {
      httpParams = httpParams.set('start_date', params.startDate.toISOString());
    }

    if (params.endDate) {
      httpParams = httpParams.set('end_date', params.endDate.toISOString());
    }

    if (params.userGroupIds.length) {
      httpParams = httpParams.set('user_group_ids', params.userGroupIds.join(','));
    }

    if (limit !== undefined) {
      httpParams = httpParams.set('limit', String(limit));
    }

    return httpParams;
  }

  private handleError(patch: Partial<IAdminEventAnalyticsState>): Observable<never> {
    this.patchState(patch);

    return EMPTY;
  }
}
